import React, {useContext, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import InfoIcon from '@material-ui/icons/Info';
import BarChartIcon from '@material-ui/icons/BarChart';
import DashboardIcon from '@material-ui/icons/Dashboard';
import PriorityHighIcon from '@material-ui/icons/PriorityHigh';
import ExtensionIcon from '@material-ui/icons/Extension';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import List from '@material-ui/core/List';
import Collapse from '@material-ui/core/Collapse';
import {withRouter} from 'react-router';
import {Divider} from '@material-ui/core';
import {useLocation} from 'react-router-dom';
import {AuthContext} from '../../../context/auth';
import {getPlanStage} from '../../../utils/productAccess';

const ORANGE = '#FF6900';
const GREY = '#A5A5A5';

const OVERVIEW_BASE = '/dq_pro/national-dashboard/overview';

// Data-driven nav — mirrors NetworkDashboardList/SchoolDashboardList's
// pattern. "Introduction" (was "Overview") avoids clashing with Baseline
// Results' own "Overview" tab (the deck's slide-26 nav mockup labels the
// Executive Summary tab "Overview", matching Network's Baseline Results
// group). Priority Actions / Learning Programs / Reassessment / Methodology
// mirror Network's own pages, adapted to National's 2-level Region/School
// hierarchy (no Country level). Items are gated by plan stage (P0/P1/LC)
// per the dashboard catalog via `packages`; an item without `packages`
// (Performance Summary) is shown to every plan, and a group left with no
// visible children is hidden.
const NATIONAL_DASHBOARD_NAV = [
    {
        code: 'introduction',
        label: 'Introduction',
        icon: InfoIcon,
        children: [
            {label: 'About COS Readiness Solution', path: `${OVERVIEW_BASE}/about-readiness-solution`, packages: ['P1']},
            {label: 'About COS Children Assessment', path: `${OVERVIEW_BASE}/about-assessment`, packages: ['P0', 'P1']},
            {label: 'About Scores',                  path: `${OVERVIEW_BASE}/about-scores`, packages: ['P0', 'P1', 'LC']},
            {label: 'About Report',                  path: `${OVERVIEW_BASE}/about-report`, packages: ['P0', 'P1']},
        ],
    },
    {
        code: 'performance_summary',
        label: 'Performance Summary',
        icon: DashboardIcon,
        path: '/dq_pro/national-dashboard/performance-summary',
    },
    {
        code: 'baseline_results',
        label: 'Baseline Results',
        icon: BarChartIcon,
        children: [
            {label: 'Overview',           path: '/dq_pro/national-dashboard/summary', packages: ['P0', 'P1']},
            {label: 'Readiness',          path: '/dq_pro/national-dashboard/competency', packages: ['P0', 'P1']},
            {label: 'Cyber Risks',        path: '/dq_pro/national-dashboard/cyber-risks', packages: ['P0', 'P1']},
            {label: 'Context & Support',  path: '/dq_pro/national-dashboard/context-equity', packages: ['P0', 'P1']},
            {label: 'Participation',      path: '/dq_pro/national-dashboard/participation', packages: ['P0', 'P1']},
        ],
    },
    {
        code: 'priority_actions',
        label: 'Priority Actions',
        icon: PriorityHighIcon,
        children: [
            {label: 'Learning Program',    path: '/dq_pro/national-dashboard/priority-actions/learning-program', packages: ['P0', 'P1']},
            {label: 'System Intervention', path: '/dq_pro/national-dashboard/priority-actions/system-intervention', packages: ['P0', 'P1']},
        ],
    },
    {
        code: 'learning_programs',
        label: 'Learning Programs',
        icon: ExtensionIcon,
        children: [
            {label: 'Learning Deployment', path: '/dq_pro/national-dashboard/learning-programs/learning-deployment', packages: ['P1', 'LC']},
            {label: 'Learning Impact',     path: '/dq_pro/national-dashboard/learning-programs/learning-impact', packages: ['P1', 'LC']},
        ],
    },
    {
        code: 'reassessment',
        label: 'Reassessment',
        icon: TrendingUpIcon,
        path: '/dq_pro/national-dashboard/reassessment',
        packages: ['P1'],
    },
    {
        code: 'methodology',
        label: 'Methodology',
        icon: MenuBookIcon,
        children: [
            {label: 'How the Assessment Is Scored',       path: '/dq_pro/national-dashboard/methodology', packages: ['P0', 'P1']},
            {label: 'How Priority Actions Are Triggered', path: '/dq_pro/national-dashboard/methodology/priority-actions', packages: ['P0', 'P1']},
            {label: 'How Learning Impact Is Measured',    path: '/dq_pro/national-dashboard/methodology/learning-impact', packages: ['P1', 'LC']},
        ],
    },
];

const useStyles = makeStyles(() => ({
    item: {
        width: '92%',
        margin: '2px 10px',
        borderRadius: '10px',
        padding: '10px 12px',
        color: GREY,
        '&:hover': {backgroundColor: 'rgba(237,102,40,0.08)'},
    },
    itemCollapsed: {
        width: '65%',
    },
    itemActive: {
        backgroundColor: ORANGE,
        color: '#ffffff',
        '&:hover': {backgroundColor: ORANGE},
    },
    backItem: {
        backgroundColor: '#000000 !important',
        '& .MuiTypography-root': {color: '#ffffff !important'},
        '&:hover': {backgroundColor: '#222222 !important'},
    },
    subItem: {
        width: '85%',
        margin: '2px 10px 2px 32px',
        borderRadius: '10px',
        padding: '8px 12px',
        color: GREY,
        '&:hover': {backgroundColor: 'rgba(237,102,40,0.08)'},
    },
    subItemActive: {
        backgroundColor: ORANGE,
        color: '#ffffff',
        '&:hover': {backgroundColor: ORANGE},
    },
    icon: {minWidth: 'unset', marginRight: '12px'},
    label: {
        '& .MuiTypography-root': {fontSize: '14px', fontWeight: 600},
    },
    divider: {margin: '4px 10px'},
}));

const NationalDashboardList = ({history, isDrawerOpen}) => {
    const classes = useStyles();
    const location = useLocation();
    const {authUser} = useContext(AuthContext);
    const pkg = getPlanStage(authUser);
    const hasAccess = (item) => !item.packages || item.packages.includes(pkg);

    const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

    const visibleGroups = NATIONAL_DASHBOARD_NAV
        .map((group) => group.children
            ? {...group, children: group.children.filter(hasAccess)}
            : group)
        .filter((group) => (group.children ? group.children.length > 0 : hasAccess(group)));

    const [openGroups, setOpenGroups] = useState(() => {
        const initial = {};
        visibleGroups.forEach((group) => {
            if (group.children) {
                initial[group.code] = group.children.some((c) => isActive(c.path));
            }
        });
        return initial;
    });
    const toggleGroup = (code) => setOpenGroups((prev) => ({...prev, [code]: !prev[code]}));

    const NavItem = ({label, icon: Icon, path, isBack}) => {
        const active = !isBack && isActive(path);
        return (
            <>
                <ListItem
                    button
                    onClick={() => history.push(path)}
                    className={`${classes.item} ${isBack ? classes.backItem : ''} ${!isDrawerOpen ? classes.itemCollapsed : ''} ${active ? classes.itemActive : ''}`}
                >
                    <ListItemIcon className={classes.icon}>
                        <Icon style={{color: (isBack || active) ? '#ffffff' : ORANGE, fontSize: '22px'}}/>
                    </ListItemIcon>
                    {isDrawerOpen && <ListItemText className={classes.label} primary={label}/>}
                </ListItem>
                <Divider className={classes.divider}/>
            </>
        );
    };

    const SubItem = ({label, path}) => {
        // Exact match only — a prefix match would wrongly light up "How the
        // Assessment Is Scored" (path .../methodology) whenever a sibling
        // sub-route like .../methodology/learning-impact is active, since
        // that path starts with this one's.
        const active = location.pathname === path;
        return (
            <ListItem
                button
                onClick={() => history.push(path)}
                className={`${classes.subItem} ${active ? classes.subItemActive : ''}`}
            >
                {isDrawerOpen && <ListItemText className={classes.label} primary={label}/>}
            </ListItem>
        );
    };

    const NavGroup = ({group}) => {
        const active = group.children.some((c) => isActive(c.path));
        const open = !!openGroups[group.code];
        const Icon = group.icon;
        return (
            <>
                <ListItem
                    button
                    onClick={() => {
                        if (isDrawerOpen) toggleGroup(group.code);
                        history.push(group.children[0].path);
                    }}
                    className={`${classes.item} ${!isDrawerOpen ? classes.itemCollapsed : ''} ${active ? classes.itemActive : ''}`}
                >
                    <ListItemIcon className={classes.icon}>
                        <Icon style={{color: active ? '#ffffff' : ORANGE, fontSize: '22px'}}/>
                    </ListItemIcon>
                    {isDrawerOpen && (
                        <>
                            <ListItemText className={classes.label} primary={group.label}/>
                            {open
                                ? <ExpandLess style={{color: active ? '#ffffff' : ORANGE}}/>
                                : <ExpandMore style={{color: active ? '#ffffff' : ORANGE}}/>
                            }
                        </>
                    )}
                </ListItem>
                <Divider className={classes.divider}/>

                <Collapse in={open && isDrawerOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        {group.children.map((c) => (
                            <SubItem key={c.path} label={c.label} path={c.path}/>
                        ))}
                    </List>
                </Collapse>
            </>
        );
    };

    return (
        <div style={{display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden'}}>
            <List style={{flexGrow: 1, paddingTop: '8px', overflowY: 'auto'}}>
                <NavItem label="Back to Workspace" icon={ArrowBackIcon} path="/my_workspace" isBack/>

                {visibleGroups.map((group) => group.children
                    ? <NavGroup key={group.code} group={group}/>
                    : <NavItem key={group.code} label={group.label} icon={group.icon} path={group.path}/>
                )}
            </List>
        </div>
    );
};

export default withRouter(NationalDashboardList);
